import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  tbodyBorder,
  theadBackground,
  theadBorder,
  theadText,
} from "../../components/common/ColorTheme";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ArrowCircleUpSharp } from "@mui/icons-material";

export interface ColumnConfig<T> {
  key: keyof T;
  label: string;
  width?: number | string;
  searchable?: boolean; // enable click-to-search
  render?: (row: T) => React.ReactNode; // custom render
}

interface GenericTableProps<T> {
  data: T[];
  columns: ColumnConfig<T>[];
  actions?: {
    edit?: (row: T) => void;
    delete?: (row: T) => void;
    moveup?: (row: T) => void;
  };
}

export function WorkflowTable<T extends Record<string, any>>({
  data,
  columns,
  actions,
}: GenericTableProps<T>) {
  const [searchValues, setSearchValues] = React.useState<
    Record<string, string>
  >({});

  const [searchMode, setSearchMode] = React.useState<Record<string, boolean>>(
    {}
  );

  const filteredData = React.useMemo(() => {
    return data.filter((row) =>
      columns.every((col) => {
        if (!col.searchable) return true;

        const searchText = searchValues[col.key as string] ?? "";
        if (!searchText) return true;

        let value: any;

        if (col.render) {
          const rendered = col.render(row);

          if (typeof rendered === "string" || typeof rendered === "number") {
            value = rendered;
          } else if (React.isValidElement(rendered)) {
            const element = rendered as React.ReactElement<any>;
            value = element.props.children ?? "";
          } else {
            value = "";
          }
        } else {
          value = row[col.key];
        }

        const valueStr = value != null ? String(value) : "";

        return valueStr.toLowerCase().includes(searchText.toLowerCase());
      })
    );
  }, [data, searchValues, columns]);

  return (
    <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: theadBackground,
              "& th": {
                color: theadText,
                fontVariant: "small-caps",
                borderRight: `1px solid ${theadBorder}`,
                "&:last-child": { borderRight: "none" },
              },
            }}
          >
            {columns.map((col) => (
              <TableCell
                key={col.key as string}
                onClick={() => {
                  if (col.searchable)
                    setSearchMode((prev) => ({
                      ...prev,
                      [col.key as string]: !prev[col.key as string],
                    }));
                }}
              >
                {searchMode[col.key as string] ? (
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder={`Search ${col.label}`}
                    fullWidth
                    value={searchValues[col.key as string] ?? ""}
                    onChange={(e) =>
                      setSearchValues((prev) => ({
                        ...prev,
                        [col.key]: e.target.value,
                      }))
                    }
                    onBlur={() =>
                      setSearchMode((prev) => ({ ...prev, [col.key]: false }))
                    }
                    autoFocus
                  />
                ) : (
                  col.label
                )}
              </TableCell>
            ))}

            {actions && <TableCell align="center">Actions</TableCell>}
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredData.map((row, idx) => (
            <TableRow key={idx}>
              {columns.map((col) => (
                <TableCell
                  key={col.key as string}
                  sx={{ borderRight: `1px solid ${tbodyBorder}` }}
                >
                  {col.render ? col.render(row) : row[col.key]}
                </TableCell>
              ))}

              {actions && (
                <TableCell align="center">
                  {idx > 0 &&
                    idx != filteredData.length - 1 &&
                    actions.moveup && (
                      <Tooltip title="Move up">
                        <IconButton
                          color="info"
                          size="small"
                          onClick={() => actions.moveup!(row)}
                        >
                          <ArrowCircleUpSharp />
                        </IconButton>
                      </Tooltip>
                    )}

                  {idx != filteredData.length - 1 && actions.edit && (
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => actions.edit!(row)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {idx > 0 &&
                    idx != filteredData.length - 1 &&
                    actions.delete && (
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => actions.delete!(row)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                </TableCell>
              )}
            </TableRow>
          ))}

          {filteredData.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columns.length + (actions ? 1 : 0)}
                align="center"
                sx={{ py: 4 }}
              >
                No records found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
